import { useId, type InputHTMLAttributes } from 'react';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Server-side message for this field, if the last submit produced one. */
  error?: string | undefined;
}

/**
 * A labelled input that can carry a server-side error.
 *
 * The error is wired through `aria-describedby` and `aria-invalid` rather than
 * only shown in red, so it reaches a screen reader as part of the field instead
 * of as loose text somewhere on the page.
 */
export function Field({ label, error, ...props }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>

      <input
        {...props}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="rounded-lg bg-current/5 px-4 py-3 outline-none ring-current/20 focus:ring-2 aria-invalid:ring-2 aria-invalid:ring-red-500/50"
      />

      {error && (
        <p id={errorId} className="text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}
